<template>
    <div ref="optionPickerWrapper" v-on-click-outside="handleOnClickOutside" class="pl-0 relative">
        <div
            class="@container cursor-pointer w-full relative"
            :data-cy="`${dataCy}-container`"
            @click="show"
        >
            <input
                ref="optionPickerInput"
                v-model="searchValue"
                class="input input-sm border border-base-300 rounded-none focus:outline-none"
                :class="(selectedAccount && !selectorIsOpen) ? 'w-0 p-0 border-0' : 'w-full'"
                :data-cy="dataCy"
                tabindex="0"
                :placeholder="placeholder"
                @focus="show"
                @click="show"
                @input="onInput"
                @keydown.shift.tab="hideAccountPicker"
                @keydown.shift.left="hideAccountPicker"
                @keydown.shift.right="hideAccountPicker"
                @keydown.shift.up="hideAccountPicker"
                @keydown.shift.down="hideAccountPicker"
                @keydown.enter="selectOption"
                @keydown.tab.exact="selectOption"
                @keydown.left.exact.prevent="activateScroller"
                @keydown.right.exact="activateDetails"
                @keydown.down.exact="focusedSide === FOCUS_SIDE.LEFT && onKeyDown()"
                @keydown.up.exact.prevent="focusedSide === FOCUS_SIDE.LEFT && onKeyUp()"
                @keydown.esc.stop="hideAccountPicker"
            >
            <div
                v-if="!selectorIsOpen && selectedAccount"
                class="input input-sm border border-base-300 rounded-none truncate overflow-hidden text-ellipsis absolute top-0  pointer-events-none w-full p-1 gap-1"
                :data-cy="dataCy + '-value'"
                :class="{'text-right': rightAlign}"
                :title="`${accountNo} ${selectedAccount.displayName}` "
            >
                <span data-cy="trans-entry-debit-account">{{ accountNo }}</span>
                <span v-if="showDisplayName" class="inline">{{ selectedAccount.displayName }}</span>
                <span v-if="subAccountLines?.length && selectedAccount.subKey">({{ subAccountLines.length }})</span>
            </div>
            <div v-else-if="selectedAccount" class="absolute top-[8px] text-lg  h-full debug right-0 i-ph-x text-base-300 hover:text-error pointer-cursor" @click="clear"/>
        </div>
        <div
            v-if="selectorIsOpen"
            ref="accountPickerContainer"
            class="account-picker-container"
            :class="[{'account-picker-single': singlePane}, openLeft ? 'open-left' : 'open-right', openTop ? 'open-top' : 'open-bottom']"
        >
            <div
                class="option-picker-left-panel"
                :class="[{focused: isFocused}, singlePane ? 'account-picker-single' : 'account-picker-double']"
            >
                <RecycleScroller
                    v-slot="{ item }"
                    ref="scroller"
                    class="w-full h-full"
                    :items="filteredOptions"
                    :item-size="showBankInfo ? 58 : 38"
                    key-field="key"
                    tabindex="-1"
                    @mouseenter="focusedSide === FOCUS_SIDE.LEFT && activateScroller"
                >
                    <div
                        :key="item.key"
                        :title="item.displayName"
                        class="new-option-picker-option"
                        :class="[{
                            selected: selectedAccount?.key === item.key,
                            highlighted: item.key === highlightedOption?.key,
                            showBanking: showBankInfo
                        }]"
                        @mousedown="selectHighlighted"
                        @mousemove="() => onMouseEnter(item)"
                        @mouseleave="focusedSide === FOCUS_SIDE.LEFT && activateScroller"
                    >
                        <slot name="option" :item="item as PickerOption">
                            <account-picker-option
                                :item="item"
                                :show-bank-info="showBankInfo"
                                :is-selected="selectedAccount?.key === item.key && selectedAccount?.subKey === item.subKey"
                                :selected-account="selectedAccount"
                                :show-display-name="showDisplayName"
                            />
                        </slot>
                    </div>
                </RecycleScroller>
                <account-picker-new-entities-actions
                    v-if="singlePaneShowActions && isFocused"
                    :search-text="searchValue"
                    @created="onNewEntityCreated"
                    @hide-picker="hideAccountPicker"
                    @keydown.left.exact="activateScroller"
                />
            </div>
            <div
                v-if="!singlePane"
                class="option-picker-right-panel"
                :class="!isFocused && 'focused'"
                @mouseenter="activateDetails"
                @mouseleave="activateScroller"
            >
                <account-picker-sub-account-details-area
                    v-if="highlightedOption?.subAccountUuid"
                    ref="accountPickerSACCDetails"
                    v-model:focus-side="focusedSide"
                    v-model:sub-account-posts="subAccountPosts"
                    v-model:sub-account-lines="subAccountLines"
                    :picker-option="{...highlightedOption, subAccountUuid: highlightedOption.subAccountUuid}"
                    class="overflow-auto"
                    @on-select-item="onSubAccountItemSelected"
                    @on-selection-complete="onSubAccountSelectionComplete"
                    @focus-scroller="activateScroller"
                />
                <account-picker-b-k-a-details-area
                    v-else-if="highlightedOption"
                    ref="accountPickerBKADetails"
                    v-model:focus-side="focusedSide"
                    :picker-option="highlightedOption"
                    class="grow overflow-auto"
                />
                <div v-else class="grow"/>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {vOnClickOutside} from '@vueuse/components'
import {SubAccount} from '@/subaccount/types/SubAccount'
import {computed, nextTick, onMounted, onUnmounted, ref, watch} from 'vue'
import {AccountOption, FOCUS_SIDE, useAccountPicker} from '@/account-picker/accountPicker.service'
import AccountPickerSubAccountDetailsArea from '@/account-picker/AccountPickerSubAccountDetailsArea.vue'
import AccountPickerBKADetailsArea from '@/account-picker/AccountPickerBKADetailsArea.vue'
import {BookkeepingAccount} from '@/bookkeeping-account/BookkeepingAccount'
import {PickerOption} from '@/components/optionPicker/PickerOption'
import {WINDOW_SCROLL_START} from '@/types/events'
import {useEmitter} from '@/services/infrastructure/emitter'
import _, {findIndex} from 'lodash'
//@ts-ignore
import {RecycleScroller} from 'vue-virtual-scroller'
import AccountPickerNewEntitiesActions from '@/account-picker/AccountPickerNewEntitiesActions.vue'
import {SubAccountLine} from '@/subaccount/outstandingBalance/SubAccountLine'
import AccountPickerOption from '@/account-picker/AccountPickerOption.vue'

const {onEvent, cleanUp} = useEmitter()

const emit = defineEmits(['onSubAccountSelectionComplete', 'onAccountPickerClosed', 'onAccountSelected'])

const props = defineProps({
    accounts: {
        type: Array<BookkeepingAccount | SubAccount>,
        required: true
    },
    placeholder: {
        type: String,
        default: 'Velg konto'
    },
    singlePane: {
        type: Boolean,
        default: false
    },
    singlePaneShowActions: {
        type: Boolean,
        default: true
    },
    showDisplayName: {
        type: Boolean,
        default: true
    },
    showBankInfo: {
        type: Boolean,
        default: false
    },
    openLeft: {
        type: Boolean,
        default: false
    },
    openTop: {
        type: Boolean,
        default: false
    },
    rightAlign: {
        type: Boolean,
        default: false
    },
    filterBySearch: {
        type: Boolean,
        default: true
    },
    startOpen: {
        type: Boolean,
        default: false
    },
    dataCy: {
        type: String,
        default: 'account-picker-input'
    }
})

//TODO: posts are never used externally, only internally against the subAccDetails area. Could we remove the posts model and let that be a ref instead?
const accountNo = defineModel<string>('accountNo')
const subAccountUuid = defineModel<string>('subAccountUuid', {required: false})
const subAccountPosts = defineModel<SubAccountLine[]>('subAccountPosts', {default: () => []})
const subAccountLines = defineModel<string[]>('subAccountLines')

const {searchValue, accountOptions, filteredOptions} = useAccountPicker(computed(() => props.accounts), props.filterBySearch)

const accountPickerContainer = ref<HTMLDivElement | null>(null)
const optionPickerWrapper = ref<HTMLDivElement | null>(null)
const optionPickerInput = ref<HTMLInputElement | null>(null)
const highlightedOption = ref<AccountOption | undefined>(undefined)

const scrollerPos = ref({top: 0, left: 0})
const scroller = ref<{ scrollToItem: Function } | null>(null)

const selectorIsOpen = ref(false)
const accountPickerSACCDetails = ref<InstanceType<typeof AccountPickerSubAccountDetailsArea> | null>(null)
const accountPickerBKADetails = ref<InstanceType<typeof AccountPickerBKADetailsArea> | null>(null)

const focusedSide = ref<FOCUS_SIDE>(FOCUS_SIDE.LEFT)

const isFocused = computed({
    get: () => focusedSide.value === FOCUS_SIDE.LEFT,
    set: (value) => {
        focusedSide.value = value ? FOCUS_SIDE.LEFT : FOCUS_SIDE.RIGHT
    }
})

watch(focusedSide, () => {
    if (focusedSide.value === FOCUS_SIDE.LEFT) {
        focusInput()
    }
})

const selectedAccount = computed<AccountOption | undefined>({
    get: () => {
        if (subAccountUuid.value) {
            return accountOptions.value.find((account) => {
                return account.subAccountUuid === subAccountUuid.value
            })
        }
        return accountOptions.value.find((account) => {
            return account.accountNo === accountNo.value
        })
    },
    set: (newAccountOption: AccountOption | undefined) => {
        if (!newAccountOption) {
            clear()
            return
        }

        accountNo.value = newAccountOption.accountNo
        subAccountUuid.value = newAccountOption.subAccountUuid
        updateSearchValue(newAccountOption)
    }
})

const clear = () => {
    accountNo.value = undefined
    subAccountUuid.value = undefined
    highlightedOption.value = undefined
    subAccountLines.value = []
    subAccountPosts.value = []
    searchValue.value = ''
}


watch(highlightedOption, () => {
    if (!highlightedOption.value) return
    isFocused.value = true
    //debounceSetOnlyFocusRef()
})

const setScrollPos = () => {
    const inputRect = optionPickerInput.value?.getBoundingClientRect() || {top: 0, left: 0, height: 32}

    const wrapperRectBottom = optionPickerWrapper.value?.getBoundingClientRect().bottom
    const breadCrumbsBottom = document.getElementById('breadcrumbs')?.getBoundingClientRect().bottom

    if (wrapperRectBottom && breadCrumbsBottom) {
        const isWrapperRectVisible = wrapperRectBottom > breadCrumbsBottom

        scrollerPos.value.top = isWrapperRectVisible ? inputRect.top + inputRect.height + 12 : breadCrumbsBottom + 12
        scrollerPos.value.left = inputRect.left
    }
}

const optionIsExactKeyMatch = (option: AccountOption) => {
    return option.key === searchValue.value
}

const onInput = () => {
    if (searchValue.value === '') {
        clear()
    } else {
        highlightBestMatch()
    }
}

const highlightBestMatch = () => {
    if (searchValue.value && filteredOptions.value.length) {
        const exactMatch = _.find(filteredOptions.value, optionIsExactKeyMatch)
        if (exactMatch) {
            highlightedOption.value = exactMatch
            return
        } else {
            //Fallback until we find something better
            highlightedOption.value = filteredOptions.value[0]
        }
    } else {
        highlightedOption.value = undefined
    }
}

const show = () => {
    isFocused.value = true
    optionPickerInput.value?.select()

    if (selectorIsOpen.value) return
    selectorIsOpen.value = true
    setScrollPos()

    highlightBestMatch()
}

const focusInput = () => optionPickerInput?.value?.focus()

const hideAccountPicker = () => {
    selectorIsOpen.value = false
    emit('onAccountPickerClosed')
}

const activateDetails = () => {
    if (!props.singlePane) {

        accountPickerSACCDetails.value?.activateFocus()
        accountPickerBKADetails.value?.activateFocus()

        isFocused.value = false
    }
}

const activateScroller = () => {
    focusInput()
    isFocused.value = true
}

const updateSearchValue = (option: AccountOption | undefined) => {
    if (option?.accountNo) {
        searchValue.value = option.key
    } else {
        searchValue.value = ''
    }
}

const selectHighlighted = () => {
    selectedAccount.value = highlightedOption.value
    if (highlightedOption.value?.subAccountUuid) {
        subAccountLines.value = subAccountPosts.value.map(post => post.uuid)
        activateDetails()
    } else {
        subAccountPosts.value = []
        subAccountLines.value = []
        hideAccountPicker()
    }
    emit('onAccountSelected', selectedAccount.value)
}

const selectOption = () => {
    if (searchValue.value === '' && !highlightedOption.value) {
        selectedAccount.value = undefined
    } else {
        selectHighlighted()
    }
    hideAccountPicker()

}

const scrollToKey = (key: string) => {
    const index = findIndex(filteredOptions.value, {key})
    scroller.value?.scrollToItem(index - 3)
}

const onKeyDown = () => {
    let existingIndex = -1
    if (highlightedOption.value) {
        existingIndex = findIndex(filteredOptions.value, {key: highlightedOption.value.key})
    }
    const nextOption = filteredOptions.value[existingIndex + 1]
    if (nextOption) {
        highlightedOption.value = nextOption
        scrollToKey(nextOption.key)
    }
}

const onKeyUp = () => {
    if (!highlightedOption.value) {
        return
    }

    const existingIndex = findIndex(filteredOptions.value, {key: highlightedOption.value.key})
    const prevOption = filteredOptions.value[existingIndex - 1]
    if (prevOption) {
        highlightedOption.value = prevOption
        scrollToKey(prevOption.key)
    }
}

const onMouseEnter = (option: AccountOption) => {
    const existingIndex = findIndex(filteredOptions.value, {key: option.key})
    const hoveredOption = filteredOptions.value[existingIndex]
    if (hoveredOption) {
        highlightedOption.value = hoveredOption
    }

    //debouncedActivateDetails()
}

const onSubAccountItemSelected = () => {
    selectHighlighted()
    activateDetails()
}

const onSubAccountSelectionComplete = () => {
    selectHighlighted()
    hideAccountPicker()
    emit('onSubAccountSelectionComplete', subAccountPosts.value)
}

const handleOnClickOutside = (event: any) => {
    const target = event.target as HTMLElement
    if (!accountPickerContainer?.value?.contains(target)) hideAccountPicker()
}

const onNewEntityCreated = (key: string) => {
    searchValue.value = key
}

onMounted(() => {
    if (selectedAccount.value) updateSearchValue(selectedAccount.value)
    onEvent(WINDOW_SCROLL_START, () => {
        if (selectorIsOpen.value) {
            setScrollPos()
        }
    })
    if (props.startOpen) {
        show()
        nextTick(() => {
            focusInput()
        })
    }

})

onUnmounted(() => {
    cleanUp()
})

</script>

<style>
@reference "@/index.css";
@reference "tailwindcss";

.account-picker-container {
    @apply absolute top-[31px] z-[200000] bg-base-100 border border-base-300 rounded shadow-xl h-[350px] w-[500px] flex;

    &.account-picker-single {
        @apply w-[400px] flex flex-col;
    }

    &.open-left {
        @apply right-0 left-auto;
    }

    &.open-right {
        @apply left-0 right-auto;
    }

    &.open-top {
        @apply bottom-8 top-auto;
    }

    &.open-bottom {
        @apply top-8 bottom-auto;
    }
}

.option-picker-left-panel {
    @apply max-h-full top-0 max-w-full shadow-md bg-base-100 min-w-20 grow-0 flex flex-col;

    &.focused {
        @apply grow w-full;
    }

    &.account-picker-single {
        @apply grow;
    }

    .new-option-picker-option {
        @apply h-[38px] pl-1 cursor-pointer flex items-center;
        &.showBanking {
            @apply h-[58px];
        }

        &.highlighted {
            @apply bg-base-200;
        }

        &.selected {
            @apply bg-primary/50;
        }
    }
}

.option-picker-right-panel {
    @apply flex flex-col bg-base-100 justify-between grow-0 shrink transition-all w-0 overflow-hidden;
    .ap-right-panel-new-entities-actions {
        @apply invisible;
    }

    &.focused {
        @apply grow w-full visible border-l;
        .ap-right-panel-new-entities-actions {
            @apply visible;
        }
    }
}

</style>